/**
 * friendlyIssueMap.js
 *
 * Mapeo de códigos técnicos del linter de outlines DMN a mensajes que un
 * usuario funcional pueda leer y accionar. El servidor sigue siendo la
 * fuente de verdad — esto es solo presentación, opt-in vía la "Vista
 * funcional" del panel de issues en `DmnGenerateModal`.
 *
 * Contrato:
 *   issue = { code, severity, message, suggestion?, section?, meta? }
 *   friendly = {
 *     title:       string,                   // qué pasa, en lenguaje del negocio
 *     body?:       string,                   // detalle / pregunta abierta
 *     suggestion?: string,                   // qué hacer
 *     actions?:    [{ id, label }],          // chips accionables (opcionales)
 *     groupKey:    string,                   // 'decisions:<name>' | 'inputs' | 'inputs:<name>' | 'bkms:<name>' | 'global'
 *     groupTitle:  string,                   // título humano del grupo
 *     hideForFunctional?: boolean,           // si true, NO mostrar al funcional
 *   }
 *
 * Cuando un código no está mapeado se devuelve un fallback que muestra el
 * `message` original (no perdemos info).
 */

const ES = {
  // ── Inputs ──────────────────────────────────────────────────────────
  INPUT_DUPLICATE: (i) => ({
    title: `El dato "${i.meta?.input ?? ''}" está declarado dos veces.`,
    suggestion: 'Renombrá uno o eliminá la copia.',
  }),
  INPUT_BAD_TYPE: (i) => ({
    title: `El tipo del dato "${i.meta?.input ?? ''}" no es válido.`,
    body: i.meta?.value ? `Pusiste "${i.meta.value}".` : undefined,
    suggestion: 'Usá uno de: texto, número, entero, sí/no, fecha.',
  }),

  // ── Decisiones / tablas ─────────────────────────────────────────────
  DECISION_DUPLICATE: (i) => ({
    title: `Hay dos decisiones llamadas "${i.meta?.decision ?? ''}".`,
    suggestion: 'Cambiale el nombre a una de las dos.',
  }),
  TABLE_MISSING_RULES: (i) => ({
    title: `La decisión "${i.meta?.decision ?? ''}" no tiene reglas.`,
    suggestion: 'Agregá al menos una regla con su resultado.',
  }),
  TABLE_RULE_WIDTH: (i) => ({
    title: `Una regla de "${i.meta?.decision ?? ''}" está incompleta.`,
    body: (i.meta?.rule_index != null)
      ? `La regla ${i.meta.rule_index + 1} tiene ${i.meta.actual} celdas, pero la tabla espera ${i.meta.expected}.`
      : undefined,
    suggestion: 'Completá las celdas que falten en esa fila.',
  }),
  TABLE_MISSING_FALLBACK: (i) => ({
    title: `"${i.meta?.decision ?? ''}" no tiene caso por defecto.`,
    body: '¿Qué pasa si ninguna regla matchea?',
    suggestion: i.meta?.output_clause
      ? `Sugerencia: agregá una fila final "Para todo lo demás → ${i.meta.output_clause} = ?".`
      : 'Sugerencia: agregá una fila final que matchee cualquier valor.',
  }),
  INPUT_REF_UNKNOWN: (i) => {
    const ref = i.meta?.ref;
    const dec = i.meta?.decision;
    const cands = i.meta?.candidates || [];
    const head = `En "${dec ?? ''}" mencionás "${ref ?? ''}" pero no figura en "Lo que necesitamos saber".`;
    if (cands.length > 0) {
      return {
        title: head,
        body: `¿Quisiste decir ${cands.map((c) => `"${c}"`).join(' o ')}?`,
      };
    }
    return {
      title: head,
      suggestion: 'Agregá el dato en "Lo que necesitamos saber" o ajustá el nombre en la decisión.',
    };
  },
  EXPR_EMPTY: (i) => ({
    title: i.meta?.entry
      ? `La fórmula de "${i.meta.entry}" en "${i.meta.decision ?? ''}" está vacía.`
      : `La fórmula de "${i.meta?.decision ?? ''}" está vacía.`,
    suggestion: 'Escribí qué valor devuelve.',
  }),
  CONTEXT_NO_ENTRIES: (i) => ({
    title: `"${i.meta?.decision ?? ''}" no tiene cálculos definidos.`,
    suggestion: 'Agregá al menos un cálculo con su nombre y fórmula.',
  }),

  // ── Hit policy / técnico oculto al funcional ───────────────────────
  HITPOLICY_INVALID: () => ({
    hideForFunctional: true,
  }),

  // ── Dependencias ────────────────────────────────────────────────────
  DEPENDENCY_UNKNOWN: (i) => {
    const ref = i.meta?.ref;
    const dec = i.meta?.decision;
    const cands = i.meta?.candidates || [];
    const head = `"${dec ?? ''}" usa "${ref ?? ''}" pero no figura como dato ni como otra decisión.`;
    if (cands.length > 0) {
      return {
        title: head,
        body: `¿Quisiste decir ${cands.map((c) => `"${c}"`).join(' o ')}?`,
      };
    }
    return {
      title: head,
      suggestion: 'Declarálo como dato de entrada o como otra decisión.',
    };
  },
  CYCLE_DETECTED: (i) => ({
    title: 'Hay un ciclo entre las decisiones.',
    body: i.meta?.cycle ? `${i.meta.cycle.join(' → ')}` : undefined,
    suggestion: 'Una decisión no puede depender (directa ni indirectamente) de sí misma.',
  }),

  // ── BKMs (técnico, oculto al funcional por defecto) ─────────────────
  BKM_PARAMS_DUPLICATE: () => ({ hideForFunctional: true }),

  // ── ML ──────────────────────────────────────────────────────────────
  ML_FALLBACK_MISSING: (i) => ({
    title: `El modelo de "${i.meta?.decision ?? ''}" no tiene plan B.`,
    body: '¿Qué valor devuelve si el modelo falla o no tiene confianza suficiente?',
    suggestion: 'Definí un valor por defecto.',
  }),
  ML_MODEL_MISSING: (i) => ({
    title: `"${i.meta?.decision ?? ''}" no indica qué modelo usar.`,
    suggestion: 'Especificá la referencia del modelo (modelRef).',
  }),
  ML_THRESHOLD_OUT_OF_RANGE: (i) => ({
    title: `El umbral de confianza de "${i.meta?.decision ?? ''}" está fuera de rango.`,
    body: i.meta?.value != null ? `Pusiste ${i.meta.value}.` : undefined,
    suggestion: 'Tiene que estar entre 0 y 1 (por ejemplo, 0.7).',
  }),

  // ── FEEL parse hints (técnico) ──────────────────────────────────────
  FEEL_PARSE_HINT: () => ({ hideForFunctional: true }),
};

const EN = {
  INPUT_DUPLICATE: (i) => ({
    title: `Input "${i.meta?.input ?? ''}" is declared twice.`,
    suggestion: 'Rename one or remove the duplicate.',
  }),
  INPUT_BAD_TYPE: (i) => ({
    title: `The type of "${i.meta?.input ?? ''}" is not valid.`,
    body: i.meta?.value ? `You wrote "${i.meta.value}".` : undefined,
    suggestion: 'Use one of: text, number, integer, true/false, date.',
  }),
  DECISION_DUPLICATE: (i) => ({
    title: `There are two decisions called "${i.meta?.decision ?? ''}".`,
    suggestion: 'Rename one of them.',
  }),
  TABLE_MISSING_RULES: (i) => ({
    title: `"${i.meta?.decision ?? ''}" has no rules.`,
    suggestion: 'Add at least one rule with its outcome.',
  }),
  TABLE_RULE_WIDTH: (i) => ({
    title: `A rule in "${i.meta?.decision ?? ''}" is incomplete.`,
    body: (i.meta?.rule_index != null)
      ? `Rule ${i.meta.rule_index + 1} has ${i.meta.actual} cells but the table expects ${i.meta.expected}.`
      : undefined,
    suggestion: 'Fill the missing cells in that row.',
  }),
  TABLE_MISSING_FALLBACK: (i) => ({
    title: `"${i.meta?.decision ?? ''}" has no default case.`,
    body: 'What happens when no rule matches?',
    suggestion: i.meta?.output_clause
      ? `Tip: add a final row "For everything else → ${i.meta.output_clause} = ?".`
      : 'Tip: add a final row that matches any value.',
  }),
  INPUT_REF_UNKNOWN: (i) => {
    const ref = i.meta?.ref;
    const dec = i.meta?.decision;
    const cands = i.meta?.candidates || [];
    const head = `In "${dec ?? ''}" you mention "${ref ?? ''}" but it's not in "What we need to know".`;
    if (cands.length > 0) {
      return { title: head, body: `Did you mean ${cands.map((c) => `"${c}"`).join(' or ')}?` };
    }
    return {
      title: head,
      suggestion: 'Add it to "What we need to know" or fix the column name.',
    };
  },
  EXPR_EMPTY: (i) => ({
    title: i.meta?.entry
      ? `The formula for "${i.meta.entry}" in "${i.meta.decision ?? ''}" is empty.`
      : `The formula for "${i.meta?.decision ?? ''}" is empty.`,
    suggestion: 'Write what value it returns.',
  }),
  CONTEXT_NO_ENTRIES: (i) => ({
    title: `"${i.meta?.decision ?? ''}" has no calculations defined.`,
    suggestion: 'Add at least one calculation with its name and formula.',
  }),
  HITPOLICY_INVALID: () => ({ hideForFunctional: true }),
  DEPENDENCY_UNKNOWN: (i) => {
    const ref = i.meta?.ref;
    const dec = i.meta?.decision;
    const cands = i.meta?.candidates || [];
    const head = `"${dec ?? ''}" uses "${ref ?? ''}" but it's neither an input nor a decision.`;
    if (cands.length > 0) {
      return { title: head, body: `Did you mean ${cands.map((c) => `"${c}"`).join(' or ')}?` };
    }
    return { title: head, suggestion: 'Declare it as an input or as another decision.' };
  },
  CYCLE_DETECTED: (i) => ({
    title: 'There is a cycle in the decision graph.',
    body: i.meta?.cycle ? `${i.meta.cycle.join(' → ')}` : undefined,
    suggestion: 'A decision cannot depend (directly or transitively) on itself.',
  }),
  BKM_PARAMS_DUPLICATE: () => ({ hideForFunctional: true }),
  ML_FALLBACK_MISSING: (i) => ({
    title: `The model in "${i.meta?.decision ?? ''}" has no fallback.`,
    body: 'What value should it return if the model fails?',
    suggestion: 'Define a default value.',
  }),
  ML_MODEL_MISSING: (i) => ({
    title: `"${i.meta?.decision ?? ''}" doesn't say which model to use.`,
    suggestion: 'Specify the modelRef.',
  }),
  ML_THRESHOLD_OUT_OF_RANGE: (i) => ({
    title: `The confidence threshold for "${i.meta?.decision ?? ''}" is out of range.`,
    body: i.meta?.value != null ? `You wrote ${i.meta.value}.` : undefined,
    suggestion: 'It must be between 0 and 1 (e.g. 0.7).',
  }),
  FEEL_PARSE_HINT: () => ({ hideForFunctional: true }),
};

const GROUP_LABELS = {
  es: {
    inputs: 'Lo que necesitamos saber',
    decisions: 'Decisiones',
    bkms: 'Cálculos reutilizables',
    dependencies: 'Cómo se conectan las decisiones',
    global: 'Otras observaciones',
  },
  en: {
    inputs: 'What we need to know',
    decisions: 'Decisions',
    bkms: 'Reusable calculations',
    dependencies: 'How decisions connect',
    global: 'Other observations',
  },
};

function _parseSection(section) {
  if (!section) return { kind: 'global', name: null };
  const [kind, ...rest] = String(section).split(':');
  return { kind, name: rest.join(':') || null };
}

/**
 * Translate a single technical issue into a friendly form.
 *
 * @param {object} issue   The raw lint issue from the backend.
 * @param {string} lang    'es' | 'en' (defaults to 'es').
 * @returns {object}       A friendly issue (see contract above).
 */
export function toFriendly(issue, lang = 'es') {
  const table = lang === 'en' ? EN : ES;
  const handler = table[issue.code];
  const labels = GROUP_LABELS[lang === 'en' ? 'en' : 'es'];
  const { kind, name } = _parseSection(issue.section);

  let groupKey;
  let groupTitle;
  if (kind === 'decisions' && name) {
    groupKey = `decisions:${name}`;
    groupTitle = name;
  } else if (kind === 'inputs') {
    groupKey = name ? `inputs:${name}` : 'inputs';
    groupTitle = labels.inputs;
  } else if (kind === 'bkms' && name) {
    groupKey = `bkms:${name}`;
    groupTitle = `${labels.bkms} — ${name}`;
  } else if (kind === 'dependencies') {
    groupKey = name ? `decisions:${name}` : 'global';
    groupTitle = name || labels.dependencies;
  } else {
    groupKey = 'global';
    groupTitle = labels.global;
  }

  let payload;
  try {
    payload = handler ? handler(issue) : null;
  } catch (_) {
    payload = null;
  }
  if (!payload) {
    // Fallback: muestra el mensaje técnico tal cual — no se pierde información,
    // pero queda explícito que es algo que aún no traducimos.
    payload = { title: issue.message, suggestion: issue.suggestion || undefined };
  }

  return {
    code: issue.code,
    severity: issue.severity,
    title:       payload.title,
    body:        payload.body,
    suggestion:  payload.suggestion,
    actions:     payload.actions,
    hideForFunctional: !!payload.hideForFunctional,
    groupKey,
    groupTitle,
  };
}

/**
 * Translate a list of issues and group them by section.
 *
 * @returns {object}  {
 *   groups: [{ key, title, issues: [friendly,...] }],
 *   hidden: number,           // cuántos se ocultaron en vista funcional
 * }
 */
export function groupFriendly(issues, lang = 'es') {
  const friendly = (issues || [])
    .map((i) => toFriendly(i, lang))
    .filter((f) => !!f);

  const visible = friendly.filter((f) => !f.hideForFunctional);
  const hidden  = friendly.length - visible.length;

  const order = [];
  const byKey = new Map();
  for (const f of visible) {
    if (!byKey.has(f.groupKey)) {
      order.push(f.groupKey);
      byKey.set(f.groupKey, { key: f.groupKey, title: f.groupTitle, issues: [] });
    }
    byKey.get(f.groupKey).issues.push(f);
  }
  return { groups: order.map((k) => byKey.get(k)), hidden };
}

export default { toFriendly, groupFriendly };
